import { useRef } from 'react';
import {
  ArrowUpTrayIcon,
  HomeIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { ROOMS } from '@/lib/roomConfig';

// Room types offered for uploads (presets carry their own). Staging-capable only.
const STAGING_ROOMS = Object.values(ROOMS).filter((r) => r.supportsStaging);

// ============================================================================
// Preset images — place WebP files in public/tryout/
// ============================================================================

export interface PresetImage {
  id: string;
  label: string;
  room: string;
  src: string;
}

export const PRESET_IMAGES: PresetImage[] = [
  { id: 'living-room', label: 'Wohnzimmer',   room: 'living_room', src: '/tryout/living-room.webp' },
  { id: 'bedroom',     label: 'Schlafzimmer', room: 'bedroom',     src: '/tryout/bedroom.webp'     },
  { id: 'kitchen',     label: 'Küche',        room: 'kitchen',     src: '/tryout/kitchen.webp'     },
  { id: 'bathroom',    label: 'Badezimmer',   room: 'bathroom',    src: '/tryout/bathroom.webp'    },
  { id: 'balcony',     label: 'Balkon',       room: 'balcony',     src: '/tryout/balcony.webp'     },
];

// ============================================================================
// Token Badge — neutral pill, design-system aligned
// ============================================================================

interface TokenBadgeProps {
  remaining: number | null;
}

export const TokenBadge: React.FC<TokenBadgeProps> = ({ remaining }) => {
  if (remaining === null) return null;

  const isEmpty = remaining === 0;
  const text = isEmpty
    ? 'Keine Versuche übrig'
    : `${remaining} kostenlose${remaining === 1 ? 'r' : ''} Versuch${remaining === 1 ? '' : 'e'} übrig`;

  return (
    <span
      className={cn(
        'inline-flex items-center h-8 px-3 rounded-full text-[13px] font-semibold',
        isEmpty
          ? 'bg-[#FEE2E2] text-[#B91C1C]'
          : 'bg-[#FFF3EC] text-[#FE6F17]',
      )}
      style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}
    >
      {text}
    </span>
  );
};

// ============================================================================
// Section Label — matches design-system §17 (Outfit SemiBold 11px, uppercase)
// ============================================================================

interface SectionLabelProps {
  icon?: React.ReactNode;
  children: React.ReactNode;
}

export const SectionLabel: React.FC<SectionLabelProps> = ({ icon, children }) => (
  <div className="flex items-center gap-1.5 mb-3 text-[#5F6B70]">
    {icon && <span className="w-[18px] h-[18px] flex-shrink-0">{icon}</span>}
    <span
      className="text-[11px] font-semibold uppercase"
      style={{ letterSpacing: '0.08em', fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}
    >
      {children}
    </span>
  </div>
);

// ============================================================================
// Primary Button — design-system §8 primary spec (bg #FE6F17, 2px #FFD4BF border)
// ============================================================================

interface PrimaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

export const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  children, trailingIcon, disabled, className, ...rest
}) => (
  <button
    disabled={disabled}
    className={cn(
      'w-full inline-flex items-center justify-center gap-2 h-12 rounded-[10px]',
      'text-[16px] font-semibold transition-all duration-150',
      disabled
        ? 'bg-[#EBEBE6] text-[#9CA3AF] border-2 border-[#EBEBE6] cursor-not-allowed'
        : 'bg-[#FE6F17] text-[#FCFAF5] border-2 border-[#FFD4BF] hover:bg-[#E65F12] hover:border-[#FFD4BF] active:bg-[#BF4E0F]',
      className,
    )}
    style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}
    {...rest}
  >
    {children}
    {trailingIcon}
  </button>
);

// ============================================================================
// Step 1 — Upload
// ============================================================================

interface TryoutStep1UploadProps {
  previewUrl: string | null;
  selectedPresetId: string | null;
  isDragOver: boolean;
  onFileSelect: (file: File) => void;
  onPresetSelect: (preset: PresetImage) => void;
  onClearImage: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onNext: () => void;
  remaining: number | null;
  presets?: PresetImage[];
  overridesReady?: boolean;
  roomType: string;
  onRoomTypeChange: (room: string) => void;
}

export const TryoutStep1Upload: React.FC<TryoutStep1UploadProps> = ({
  previewUrl,
  selectedPresetId,
  isDragOver,
  onFileSelect,
  onPresetSelect,
  onClearImage,
  onDragOver,
  onDragLeave,
  onDrop,
  onNext,
  remaining,
  presets = PRESET_IMAGES,
  overridesReady = true,
  roomType,
  onRoomTypeChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
    e.target.value = '';
  };

  const hasImage = !!previewUrl;

  return (
    <div className="space-y-6" style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}>
      {/* Upload zone or preview */}
      {!hasImage ? (
        <div
          role="button"
          tabIndex={0}
          className={cn(
            'relative border-2 border-dashed rounded-[20px] p-10 text-center transition-all duration-150 cursor-pointer select-none',
            isDragOver
              ? 'border-[#FE6F17] bg-[#FFF3EC]'
              : 'border-[#EBEBE6] hover:border-[#FE6F17]/60 hover:bg-[#FCFAF5]',
          )}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#FFF3EC] flex items-center justify-center">
              <ArrowUpTrayIcon className="w-7 h-7 text-[#FE6F17]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[16px] font-semibold text-[#001116]">
                Eigenes Foto hochladen
              </p>
              <p className="text-[14px] text-[#5F6B70] mt-1">
                Ziehe eine Datei hierher oder klicke zum Auswählen
              </p>
              <p className="text-[12px] text-[#9CA3AF] mt-2">
                JPG, PNG oder HEIC · max. 15 MB
              </p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileInput}
          />
        </div>
      ) : (
        <div className="relative rounded-[20px] overflow-hidden bg-[#F5F4F0] aspect-video border border-[#EBEBE6]">
          <img
            src={previewUrl}
            alt="Vorschau"
            className="w-full h-full object-cover"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
          <button
            onClick={onClearImage}
            className="absolute top-3 right-3 w-9 h-9 bg-white/95 hover:bg-white text-[#001116] rounded-full flex items-center justify-center transition-colors shadow-md border border-[#EBEBE6]"
            aria-label="Bild entfernen"
          >
            <XMarkIcon className="w-5 h-5" strokeWidth={1.75} />
          </button>
          {selectedPresetId && (
            <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm text-[#001116] text-[12px] font-semibold px-3 py-1 rounded-full border border-[#EBEBE6]">
              {presets.find(p => p.id === selectedPresetId)?.label}
            </div>
          )}
        </div>
      )}

      {/* Room type — only for own uploads; presets carry their own room. */}
      {previewUrl && !selectedPresetId && (
        <div>
          <SectionLabel icon={<HomeIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />}>
            Raumtyp
          </SectionLabel>
          <select
            value={roomType}
            onChange={(e) => onRoomTypeChange(e.target.value)}
            className="w-full h-12 rounded-[12px] border border-[#EBEBE6] bg-white px-4 text-[15px] text-[#001116] outline-none focus:border-[#FE6F17] focus:ring-[3px] focus:ring-[#FE6F17]/20"
          >
            {STAGING_ROOMS.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Preset images */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>Oder nimm ein Beispielbild</SectionLabel>
        </div>
        {!overridesReady ? (
          // Wait for admin overrides before painting thumbnails, otherwise an
          // overridden preset flashes its old default image first.
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-[14px] bg-[#F5F4F0] animate-pulse" style={{ aspectRatio: '1 / 1' }} />
            ))}
          </div>
        ) : (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {presets.map((preset) => {
            const active = selectedPresetId === preset.id;
            return (
              <button
                key={preset.id}
                onClick={() => onPresetSelect(preset)}
                className={cn(
                  'group relative block w-full overflow-hidden rounded-[14px] transition-all duration-150',
                  active
                    ? 'ring-[3px] ring-[#FE6F17] ring-offset-0'
                    : 'ring-1 ring-[#EBEBE6] hover:ring-[#FE6F17]/50',
                )}
                style={{ aspectRatio: '1 / 1' }}
                aria-pressed={active}
              >
                {/* Full-bleed image */}
                <img
                  src={preset.src}
                  alt={preset.label}
                  className={cn(
                    'transition-transform duration-300',
                    active ? 'scale-[1.04]' : 'group-hover:scale-[1.04]',
                  )}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                  draggable={false}
                />
                {/* Gradient for legibility */}
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 via-black/15 to-transparent pointer-events-none" />
                {/* Label overlay */}
                <div className="absolute inset-x-0 bottom-0 px-3 py-2.5 text-left">
                  <span className="text-[14px] font-semibold text-white drop-shadow-sm">
                    {preset.label}
                  </span>
                </div>
                {/* Active badge */}
                {active && (
                  <div className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-[#FE6F17] border-2 border-white flex items-center justify-center shadow-sm">
                    <svg viewBox="0 0 24 24" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        )}
      </div>

      {/* Next button — starts the emptying directly */}
      <PrimaryButton
        onClick={onNext}
        disabled={!hasImage || remaining === 0}
        trailingIcon={<SparklesIcon className="w-5 h-5" strokeWidth={1.75} />}
      >
        Leer räumen
      </PrimaryButton>

      {/* Hint when no image yet */}
      {!hasImage && (
        <p className="text-center text-[12px] text-[#9CA3AF] -mt-2">
          Wähle ein Bild, um fortzufahren
        </p>
      )}

      {/* Token remaining note */}
      {remaining !== null && (
        <div className="flex items-center justify-center pt-1">
          <TokenBadge remaining={remaining} />
        </div>
      )}
    </div>
  );
};
